import { CheckCircle, ArrowCounterClockwise, ListMagnifyingGlass } from "@phosphor-icons/react"
import { Button } from "~/components/reusable/Button"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import { useUser } from "~/providers/userProvider"
import { api } from "~/utils/api"

function nVariants(n: number) {
  return `${n} variant${n !== 1 ? "s" : ""}`
}

type Props = {
  selectedVariantIds: string[]
  clearSelected: () => void
  onReviewSelected?: (variantIds: string[]) => void
}

export function ReviewStatusButtons({
  selectedVariantIds,
  clearSelected,
  onReviewSelected,
}: Props) {
  const utils = api.useUtils()
  const { permissions } = useUser()

  const { data: reviewedIds } = api.variantMetadata.getReviewedVariantIds.useQuery(
    { variantIds: selectedVariantIds },
    { enabled: selectedVariantIds.length > 0 },
  )

  const selectedCount = selectedVariantIds.length
  const reviewedCount = reviewedIds?.length ?? 0
  const pendingCount = selectedCount - reviewedCount
  const isLoading = reviewedIds === undefined && selectedCount > 0

  const invalidate = () => utils.variantMetadata.getReviewedVariantIds.invalidate()

  const { mutate: markReviewed, isPending: isMarking } =
    api.variantMetadata.markVariantsReviewed.useMutation({
      onSettled: invalidate,
      onSuccess: clearSelected,
    })

  const { mutate: markPending, isPending: isUnmarking } =
    api.variantMetadata.unmarkVariantsReviewed.useMutation({
      onSettled: invalidate,
      onSuccess: clearSelected,
    })

  return (
    <>
      {onReviewSelected && (
        <Button
          size="sm"
          variant="neutral"
          disabled={selectedCount === 0}
          onClick={() => onReviewSelected(selectedVariantIds)}
        >
          <ListMagnifyingGlass />
          Review selected
        </Button>
      )}
      <ConfirmationDialog
        title="Mark as reviewed"
        description={`Mark ${nVariants(pendingCount)} as reviewed?`}
        confirmText="Mark as reviewed"
        disabled={!permissions.canEdit || isLoading || pendingCount === 0}
        disabledTooltip={
          !permissions.canEdit
            ? "Read-only users cannot change review status"
            : "All selected variants are already reviewed"
        }
        onConfirm={() => markReviewed({ variantIds: selectedVariantIds })}
        loading={isMarking}
        trigger={
          <Button size="sm" variant="neutral">
            <CheckCircle />
            Mark as reviewed
          </Button>
        }
      />
      <ConfirmationDialog
        title="Clear reviewed"
        description={`Clear reviewed status for ${nVariants(reviewedCount)}?`}
        confirmText="Clear reviewed"
        disabled={!permissions.canEdit || isLoading || reviewedCount === 0}
        disabledTooltip={
          !permissions.canEdit
            ? "Read-only users cannot change review status"
            : "No selected variants are reviewed"
        }
        onConfirm={() => markPending({ variantIds: selectedVariantIds })}
        loading={isUnmarking}
        trigger={
          <Button size="sm" variant="neutral">
            <ArrowCounterClockwise />
            Clear reviewed
          </Button>
        }
      />
    </>
  )
}
